import { PatchWorkingDocumentModel } from "models/PatchWorkingDocumentModel";
import { WorkingDocumentModel } from "models/WorkingDocumentModel";
import { useApi } from "providers";
import React, { useCallback, useEffect, useRef, useState } from "react";

interface EditDocumentNameProps {
  document: WorkingDocumentModel;
  onRefresh: () => void;
  onCancel: () => void;
}

export const EditDocumentName: React.FC<EditDocumentNameProps> = ({ document, onRefresh, onCancel }) => {
  const { workingDocumentsApi } = useApi();

  const inputRef = useRef<HTMLInputElement>(null);
  const { fileExtension = "", fileName = "" } = document;
  const fileExtensionWithDot = fileExtension.includes(".") ? fileExtension : `.${fileExtension}`;
  const fileNameWithoutExtension = fileName.replace(fileExtensionWithDot, "");
  const [value, setValue] = useState(fileNameWithoutExtension);

  const canSave = useCallback(
    () => value.length > 0 && value !== fileNameWithoutExtension,
    [value, fileNameWithoutExtension]
  );

  const onSave = useCallback(() => {
    if (!canSave()) return;

    const newFileName = `${value}${fileExtensionWithDot}`;
    const patch: PatchWorkingDocumentModel = {
      id: document.id,
      fileName: newFileName,
      status: "Default",
    };

    workingDocumentsApi.update(patch).then(() => onRefresh?.());
  }, [canSave, fileExtensionWithDot, value, document.id, workingDocumentsApi, onRefresh]);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => {
      if (e.key === "Enter") {
        e.preventDefault();
        onSave();
      }
      if (e.key === "Esc") {
        e.preventDefault();
        onCancel?.();
      }
    };

    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onCancel, onSave]);

  return (
    <>
      <td colSpan={3}>
        <div className="d-flex text-nowrap">
          <input
            ref={inputRef}
            type="text"
            value={value}
            onChange={(e) => setValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === "Enter") {
                onSave();
              }
              if (e.key === "Escape") {
                onCancel();
              }
            }}
            className="form-control"
          />
          <div className="ml-1 mt-2">{fileExtensionWithDot}</div>
        </div>
      </td>
      <td className="actions document-status">
        <div className="actions">
          <button className="btn btn-link" onClick={() => onCancel?.()}>
            <i className="ms-Icon ms-Icon--Cancel" />
          </button>
          <button className="btn btn-link" onClick={() => onSave()} disabled={!canSave()}>
            <i className="ms-Icon ms-Icon--Save" />
          </button>
        </div>
      </td>
    </>
  );
};
